import {sleep} from '@benchling/js-utils';

import EntryActions from 'actions/EntryActions';
import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SourceDAO from 'file-browser/SourceDAO';
import {
  ReviewerType,
  ReviewLifecycleActionType,
  ReviewLifecycleTerminalState,
  ReviewStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as metrics from 'metrics/Metrics';
import ReviewConstants, {
  COMMENT as REVIEW_CONSTANTS_COMMENT_ICON,
  IN_PROGRESS_FILTER,
  ReviewAction,
  ReviewSummary,
} from 'notebook/reviews/ReviewConstants';
import * as util from 'notebook/tables/excess/Util';
import {NoteRuntimeSnapshot} from 'notebook/tables/TableStoreTransforms';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {ALL_RESULTS_QUERY, SearchResponse} from 'ui-library/entity-input/EntityInputWithSearch';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import escapeRegExp from 'util/escapeRegExp';
import {assertNever} from 'util/Types';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';

import {REVIEW_COMPLETION_STATUS_TO_DISPLAY_TEXT} from './review-lifecycles/ReviewLifecycleConstants';

const BUTTON_STYLE_BY_ACTION = {
  [ReviewAction.REJECT]: ButtonStyle.NEGATIVE,
  [ReviewAction.SEND_FOR_REVIEW]: ButtonStyle.TERTIARY_GRAY,
  [ReviewAction.ACCEPT]: ButtonStyle.PRIMARY,
  [ReviewAction.RETRACT]: ButtonStyle.NEGATIVE,
  [ReviewAction.COMMENT]: ButtonStyle.TERTIARY_GRAY,
};

const REVIEW_LIFECYCLE_ACTION_TITLES = {
  ACCEPT: 'Accepting',
  APPROVE: 'Approving',
  COMPLETE: 'Completing',
  REVIEW: 'Reviewing',
  SELF_REVIEW: 'Self-Reviewing',
  WITNESS: 'Witnessing',
};

function canUserReviewTemplate(type: string): boolean {
  return (
    licenses.hasLicense('LICENSE_REVIEWS') &&
    ([ModelTypeConstants.STAGE_CONFIGURATION, ModelTypeConstants.TEMPLATE] as Array<string>).includes(type)
  );
}

function isTypeReviewable(type: string): boolean {
  return ([ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY] as Array<string>).includes(type);
}

/**
 * When evaluation snapshots and/or persist eval save are enabled, ensure that a NoteRuntimeSnapshot has
 * been persisted to the DB and associated with the entry prior to submitting for review. This function
 * encapsulates the two steps required to accomplish that goal:
 * 1. Wait for the existing NoteRuntimeSnapshot to be completely resolved (or generate a new snapshot of one
 *    does not exist).
 * 2. Trigger a entry update (persist) cycle and wait for it to complete.
 *
 * If these steps cannot complete within two minutes, an exception is thrown.
 *
 * @param {string} entryId
 */
export async function ensureCurrentRuntimeSnapshotPersisted(entryId: string): Promise<void> {
  if (
    !BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_SAVE ||
    !(APIUtil.apiIdToType(entryId) === ModelTypeConstants.ENTRY)
  ) {
    return;
  }

  // This should never throw; the entry should always be in the store when a user is submitting for reivew.
  if (!EntryStore.inStore(entryId)) {
    throw new Error('Snapshot resolution failed');
  }

  const start = new Date().getTime();
  const timeoutMs = 120000;

  // Wait for the snapshot to be successfully saved before resolving.
  // If persist eval is enabled, we don't want to force-generate a new NoteRuntimeSnapshot, as that could
  // lead to submitting the entry for review with data that differs from what the user is seeing. If persist
  // eval is not enabled, we need to synchronously create a new snapshot so that it can be persisted.
  if (BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_SAVE) {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (_isEvaluationSnapshotFinishedLoading(EntryStore.getEntry(entryId).note.noteRuntimeSnapshot)) {
        metrics.gauge('EvaluationSnapshots.awaitEntrySnapshotResolution', new Date().getTime() - start, {});
        break;
      }
      if (new Date().getTime() - start >= timeoutMs) {
        throw new Error('Snapshot query resolution timed out');
      }
      await sleep(1000);
    }
  } else {
    await EntryActions.generateEvaluationSnapshot(entryId);
    metrics.gauge('EvaluationSnapshots.awaitEntrySnapshotGeneration', new Date().getTime() - start, {});
  }

  // Kick off an entry update with `bumpVersion` set to true. If the entry does not have local edits, this
  // will not result in a new entry version. However, if there are unsaved changes (i.e. a bootstrapped
  // NoteRuntimeSnapshot), this line will cause a new entry version to be persisted with the changes.
  const saveStart = new Date().getTime();
  EntryActions.update(entryId, {$merge: {}}, {bumpVersion: true});

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!EntryStore.hasUnsavedChanges(entryId)) {
      metrics.gauge(
        'EvaluationSnapshots.persistEntryWithSnapshotForReview',
        new Date().getTime() - saveStart,
        {}
      );
      return;
    }
    if (new Date().getTime() - start >= timeoutMs) {
      throw new Error('Snapshot save resolution timed out');
    }
    await sleep(1000);
  }
}

function _isEvaluationSnapshotFinishedLoading(noteRuntimeSnapshot: NoteRuntimeSnapshot): boolean {
  if (noteRuntimeSnapshot.excessVersion == null || noteRuntimeSnapshot.evaluationSnapshot == null) {
    return false;
  }

  return !util.hasLoadingCells(noteRuntimeSnapshot.evaluationSnapshot);
}

export function getReviewFromStore(fileId: string): ReviewSummary | null {
  // TODO(T9225) Use ReviewStore
  if (EntryStore.inStore(fileId)) {
    return EntryStore.getEditorState(fileId).entry.review;
  } else if (StageEntryStore.inStore(fileId)) {
    return StageEntryStore.getEditorState(fileId).stageEntryDocument.review ?? null;
  } else {
    return null;
  }
}

export function canUserReviewType(type: string): boolean {
  return licenses.hasLicense('LICENSE_REVIEWS') && isTypeReviewable(type);
}

export function hasReviewsEnabled(type: string): boolean {
  if (type === ModelTypeConstants.TEMPLATE) {
    return BenchlingConfig.ENABLE_ELN_REVIEW_CRITERIA;
  }
  return licenses.hasLicense('LICENSE_REVIEWS') && isTypeReviewable(type);
}

export function getDefaultReviewComment(
  action: ReviewAction | ReviewLifecycleActionType,
  isForReviewlifeCycleAction: boolean = false
): string {
  // Legacy auditor-based reviews will not have default review comments when ENABLE_ELN_REVIEW_COMMENTS is disabled. Otherwise this will block them from sending for review.
  const isLegacyReviewWithDisabledComments =
    !isForReviewlifeCycleAction && !BenchlingConfig.ENABLE_ELN_REVIEW_COMMENTS;
  if (isLegacyReviewWithDisabledComments) {
    return '';
  }
  return BenchlingConfig.ELN_REVIEW_DEFAULT_COMMENTS[action] || '';
}

export function getReviewLifecycleActionTitle(rlcAction: ReviewLifecycleActionType): string {
  return REVIEW_LIFECYCLE_ACTION_TITLES[rlcAction] || '';
}

export function actionToButtonStyle(action: ReviewAction): ButtonStyle {
  return BUTTON_STYLE_BY_ACTION[action];
}

export function getIconNameFromAction(action: ReviewAction): BenchlingIconName {
  switch (action) {
    case ReviewAction.ACCEPT:
      return ReviewStatus.ACCEPTED;
    case ReviewAction.REJECT:
      return ReviewStatus.REJECTED;
    case ReviewAction.RETRACT:
      return ReviewStatus.RETRACTED;
    case ReviewAction.SEND_FOR_REVIEW:
      return ReviewStatus.NEEDS_REVIEW;
    case ReviewAction.COMMENT:
      return REVIEW_CONSTANTS_COMMENT_ICON;
    default:
      assertAndContinue(false, `Unexpected review action: ${assertNever(action)}`);
      return 'notdef';
  }
}

export function getEditWarning(status: ReviewStatus): string | null {
  switch (status) {
    case ReviewConstants.REVIEW_SNAPSHOT_IN_PROGRESS:
      return 'Editing is disabled because this entry is currently being snapshotted for review.';
    case ReviewConstants.ACTION_REQUIRED:
    case ReviewConstants.NEEDS_REVIEW:
      return 'Editing is disabled because this entry is currently under review.';
    case ReviewConstants.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
      return 'Editing is disabled because this entry is currently being snapshotted after acceptance.';
    case ReviewConstants.ACCEPTED:
      return 'Editing is disabled because this entry has already been accepted.';
    default:
      throw new Error(`Unexpected review status: ${status}`);
  }
}

export function getStatusCSSClassFromReview(status?: ReviewStatus): string | null {
  // This could eventually take in the current user, and show different colors based on
  // if the current user is an entry author or not.
  switch (status) {
    case ReviewStatus.REJECTED:
      return 'is-rejected';
    case ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS:
    case ReviewStatus.NEEDS_REVIEW:
      return 'is-needsReview';
    case ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
    case ReviewStatus.ACCEPTED:
      return 'is-accepted';
    case ReviewStatus.RETRACTED:
      return 'is-retracted';
    case null:
    case undefined:
      return 'is-inProgress';
    default:
      throw new Error(`Unexpected review status: ${status}`);
  }
}

// This could eventually take in the current user, and show different colors based on if the current user is an entry author or not.
export function getStatusIcon(
  status?: ReviewStatus,
  defaultStatusIcon?: BenchlingIconName
): BenchlingIconName {
  // Uses defaultStatusIcon if available, otherwise in progress icon.
  // This is only used when review has not started or the review has been retracted
  if (!status || status === ReviewConstants.RETRACTED) {
    return defaultStatusIcon || IN_PROGRESS_FILTER;
  }
  return status;
}

// Keep in sync with benchling/notebook/reviews/models/review.py::get_status_display_text
export function getStatusTextFromReview(
  status: ReviewStatus | undefined,
  terminalState?: ReviewLifecycleTerminalState,
  defaultText?: string
): string | null {
  const acceptanceText = terminalState ? REVIEW_COMPLETION_STATUS_TO_DISPLAY_TEXT[terminalState] : 'Accepted';
  switch (status) {
    case ReviewConstants.REJECTED:
      return 'Needs revision';
    case ReviewConstants.ACTION_REQUIRED:
      return 'Action required';
    case ReviewConstants.REVIEW_SNAPSHOT_IN_PROGRESS:
      // A reviewable is not formally "sent for review" until the snapshot succeeds.
      return 'Sent for review (pending snapshot)';
    case ReviewConstants.NEEDS_REVIEW:
      return 'Needs review';
    case ReviewConstants.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
      // A reviewable is not formally "accepted" until the snapshot succeeds.
      return `${acceptanceText} (pending snapshot)`;
    case ReviewConstants.ACCEPTED:
      return acceptanceText;
    case ReviewConstants.RETRACTED:
    case null:
    case undefined:
      return defaultText ?? 'In progress';
    default:
      throw new Error(`Unexpected review status: ${status}`);
  }
}

export {isTypeReviewable, canUserReviewTemplate};

export function searchForReviewers(
  query: string,
  reviewerType: ReviewerType,
  sourceId: string,
  authors: Array<MinimalEntity> | undefined,
  selectedTeamId: string | undefined
): Promise<SearchResponse> {
  switch (reviewerType) {
    case ReviewerType.TEAM: {
      assert(
        selectedTeamId != null,
        'Reviewer type is team but there is no selected team on the review lifecycle stage'
      );
      const teamIds = [selectedTeamId];
      return SourceDAO.searchCollaboratorsForUsers(sourceId, query, teamIds).then((response) => ({
        entitySummaries: response.entitySummaries?.filter(
          (value) => authors?.find((author) => author.id === value.id) == null
        ),
      }));
    }
    case ReviewerType.PROJECT_MEMBERS:
      return SourceDAO.searchCollaboratorsForUsers(sourceId, query).then((response) => ({
        entitySummaries: response.entitySummaries?.filter(
          (value) => authors?.find((author) => author.id === value.id) == null
        ),
      }));
    case ReviewerType.AUTHOR: {
      let authorEntities = authors;
      // context: query string is hard-coded to ALL_RESULTS_QUERY if it is an empty string
      //          query string needs to be hard-coded to ALL_RESULTS_QUERY for above DAO searches to work
      // only perform author search if query string is not ALL_RESULTS_QUERY
      if (query !== ALL_RESULTS_QUERY) {
        authorEntities = authors?.filter((author) =>
          author.name!.match(new RegExp(escapeRegExp(query), 'i'))
        );
      }

      return Promise.resolve({
        entitySummaries: authorEntities?.map((user) => ({
          id: user.id,
          avatar_url: user.avatarUrl ?? '',
          handle: user.handle,
          name: user.name ?? '',
          joinedOn: '',
          url_identifier: '',
          email: '',
          academic: false,
          institution: null,
          isTenantAdmin: false,
          proclaimed: null,
          status: null,
          suspended: false,
          type: ModelTypeConstants.USER,
          text: '',
        })),
      });
    }
    default:
      assert(false, `Unexpected reviewerType: ${assertNever(reviewerType)}`);
  }
}

/**
 * Return whether the reviewable's status is one from which the reviewable can be sent for review.
 *
 * In other words, is `NEEDS_REVIEW` a valid subsequent state transition from the current state?
 *
 * @param reviewStatus - Ideally passed in as `review?.status`. Should be `undefined` if entry has never been
 *                       sent for review.
 */
export function canSendForReviewFromStatus(reviewStatus?: ReviewStatus): boolean {
  return reviewStatus == null || [ReviewStatus.REJECTED, ReviewStatus.RETRACTED].includes(reviewStatus);
}

export async function refreshFrozenChipVersion(entryId: string, reviewStatus: ReviewStatus): Promise<void> {
  const entryType = APIUtil.apiIdToType(entryId);
  // TODO(BNCH-104019): Support frozen chip version refresh in Worksheet Documents
  if (entryType !== ModelTypeConstants.ENTRY) {
    // Currently no support for FrozenChips in non-entries,
    // So no need to perform a refresh as no version will be generated
    return;
  }
  let loadingMessage;
  if (reviewStatus === ReviewStatus.NEEDS_REVIEW) {
    loadingMessage = 'Sending for Review';
  } else if (reviewStatus === ReviewStatus.ACCEPTED) {
    loadingMessage = 'Accepting Review';
  } else {
    loadingMessage = 'Retracting Review';
  }
  LoadingScreen.show(loadingMessage);
  await EntryActions.open(entryId);
  LoadingScreen.hide();
}
